import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadForm = ({ variant = "full" }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    property_type: "",
    employee_count: "",
    machine_type: "",
    message: "",
  });

  const propertyTypes = [
    "Office Building",
    "Apartment Complex",
    "Commercial Property",
    "Hospital/Healthcare",
    "Fitness Center",
    "University/School",
    "Retail Space",
    "Manufacturing Facility",
    "Other",
  ];

  const employeeCounts = [
    "1-25",
    "26-50",
    "51-100",
    "101-250",
    "251-500",
    "500+",
  ];

  const machineTypes = [
    "Drink Machine",
    "Snack Machine",
    "Combo Machine",
    "Micro Market",
    "Not Sure Yet",
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/leads`, formData);
      toast.success("Thanks for reaching out! We'll be in touch soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        company_name: "",
        property_type: "",
        employee_count: "",
        machine_type: "",
        message: "",
      });
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="lead-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <div>
          <label className="block font-bold text-navy mb-2" htmlFor="name">
            Full Name *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="input-retro w-full"
            placeholder="John Smith"
            data-testid="lead-form-name"
          />
        </div>

        {/* Email */}
        <div>
          <label className="block font-bold text-navy mb-2" htmlFor="email">
            Email Address *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="input-retro w-full"
            placeholder="john@company.com"
            data-testid="lead-form-email"
          />
        </div>

        {/* Phone */}
        <div>
          <label className="block font-bold text-navy mb-2" htmlFor="phone">
            Phone Number *
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            className="input-retro w-full"
            placeholder="(555) 123-4567"
            data-testid="lead-form-phone"
          />
        </div>

        {/* Company Name */}
        <div>
          <label className="block font-bold text-navy mb-2" htmlFor="company_name">
            Company Name
          </label>
          <input
            type="text"
            id="company_name"
            name="company_name"
            value={formData.company_name}
            onChange={handleChange}
            className="input-retro w-full"
            placeholder="Acme Corp"
            data-testid="lead-form-company"
          />
        </div>

        {/* Property Type */}
        <div>
          <label className="block font-bold text-navy mb-2">Property Type *</label>
          <Select
            value={formData.property_type}
            onValueChange={(value) => handleSelectChange("property_type", value)}
            required
          >
            <SelectTrigger className="input-retro w-full" data-testid="lead-form-property-type">
              <SelectValue placeholder="Select property type" />
            </SelectTrigger>
            <SelectContent className="bg-white border-2 border-navy">
              {propertyTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Employee Count */}
        <div>
          <label className="block font-bold text-navy mb-2">
            Number of Employees/Tenants
          </label>
          <Select
            value={formData.employee_count}
            onValueChange={(value) => handleSelectChange("employee_count", value)}
          >
            <SelectTrigger className="input-retro w-full" data-testid="lead-form-employee-count">
              <SelectValue placeholder="Select range" />
            </SelectTrigger>
            <SelectContent className="bg-white border-2 border-navy">
              {employeeCounts.map((count) => (
                <SelectItem key={count} value={count}>
                  {count}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Machine Type */}
        <div className="md:col-span-2">
          <label className="block font-bold text-navy mb-2">
            Preferred Machine Type
          </label>
          <Select
            value={formData.machine_type}
            onValueChange={(value) => handleSelectChange("machine_type", value)}
          >
            <SelectTrigger className="input-retro w-full" data-testid="lead-form-machine-type">
              <SelectValue placeholder="Select machine type" />
            </SelectTrigger>
            <SelectContent className="bg-white border-2 border-navy">
              {machineTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Message */}
        {variant === "full" && (
          <div className="md:col-span-2">
            <label className="block font-bold text-navy mb-2" htmlFor="message">
              Additional Information
            </label>
            <Textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              className="input-retro w-full min-h-[120px]"
              placeholder="Tell us about your space, specific needs, or any questions..."
              data-testid="lead-form-message"
            />
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full flex items-center justify-center gap-2"
        data-testid="lead-form-submit"
      >
        {loading ? (
          <>
            <Loader2 className="h-5 w-5 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="h-5 w-5" />
            Get Your Free Consultation
          </>
        )}
      </button>
    </form>
  );
};

export default LeadForm;
